/**
 * `/m/join` — the landing page for a mobile enrollment link (#1244).
 *
 * The invitation email carries this URL alongside the browser one, so an invited
 * mechanic can go from their inbox to a signed-in app without an admin holding
 * up a screen to scan. It is a plain https link because that is the only kind a
 * mail client will reliably render and the only kind that does something useful
 * when the email is read on a desktop; turning it into an app is this page's job.
 *
 * **Reaching this page at all means the app did not intercept the link.** With
 * Universal Links active, iOS opens the app on the confirm screen and this page
 * never renders. So everything below is what to do *instead*, and there is
 * deliberately **no `os-buddy://` button**: a custom scheme has no owner, so
 * emitting the payload into one hands it to whichever app claimed the scheme. The
 * full reasoning — including why a per-kind version of that check failed open — is
 * in the comment above the alert below, and in design §5.5.
 *
 * Two ways out, in the order they help:
 *
 * 1. **Scan it** — the same payload as a QR, shown only from `md` up. Someone
 *    reading their email on a workshop PC cannot tap a link into their phone,
 *    and this is the path that costs them nothing. On a phone it would be a QR
 *    you cannot scan with the device displaying it, so it is hidden there. The
 *    reader's own camera is the receiver, which is what makes this safe where a
 *    scheme handoff is not.
 * 2. **Continue in the browser** — for an invitation, the existing
 *    `/accept-invitation` flow, carrying only the extracted token. This page must
 *    never be a dead end just because the app did not open.
 *
 * **Nothing here is sent anywhere.** The fragment holds a one-time invitation
 * token, and it lives in the fragment precisely so it stays in the browser: no
 * fetch, no analytics, no error reporting, and the payload is never written into
 * the query string or the document title. The one navigation that carries the
 * token — the browser fallback — is the same URL the email's primary button
 * already uses, so it adds no exposure that was not there before.
 *
 * The details it displays come off an **attacker-suppliable link**: anyone can
 * mail one. So the workshop name is labelled as coming from the link, the API
 * host is shown for a human to recognise, and neither is treated as authority —
 * the app re-validates the host against its compiled-in allowlist before any
 * request, and the name it persists comes from the server's response.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { MobileJoinQr } from '@/components/settings/MobileJoinQr';
import { CopyableCode } from '@/components/common/CopyableCode';
import { useBrandingStore } from '@/stores/brandingStore';
import { parseMobileJoinPayload } from './mobileJoinPayload';
import { formatDate } from '@/utils/locale';

/**
 * Longest workshop label rendered from the link.
 *
 * staff-api already truncates `name=` to 48 characters, so this only bites on a
 * hand-crafted link — which is exactly the case worth bounding, since an
 * unbounded attacker-supplied string in a heading can push the host and the
 * buttons off the screen and turn "read the host before you continue" into
 * advice nobody can follow.
 */
const NAME_DISPLAY_MAX = 48;

function truncate(value: string, max: number): string {
  const points = Array.from(value);
  return points.length <= max ? value : `${points.slice(0, max).join('')}…`;
}

export default function MobileJoinPage() {
  const { t } = useTranslation('common');
  const brandedName = useBrandingStore((s) => s.branding?.branding?.companyName);
  const { hash } = useLocation();

  // `location.hash` keeps its leading '#'; the parser accepts either form.
  const parse = useMemo(() => parseMobileJoinPayload(hash), [hash]);

  const appName = t('appName');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            color="primary"
            textAlign="center"
            gutterBottom
          >
            {brandedName || appName}
          </Typography>

          {parse.status === 'invalid' && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Alert severity="error">{t('mobileJoin.invalid')}</Alert>
              <Typography variant="body2" color="text.secondary">
                {t('mobileJoin.invalidHint')}
              </Typography>
            </Stack>
          )}

          {parse.status === 'unsupportedVersion' && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Alert severity="warning">{t('mobileJoin.unsupportedVersion')}</Alert>
              <Typography variant="body2" color="text.secondary">
                {t('mobileJoin.unsupportedVersionHint', { app: appName })}
              </Typography>
            </Stack>
          )}

          {parse.status === 'expired' && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Alert severity="warning">
                {t('mobileJoin.expired', {
                  date: formatDate(parse.expiredAt),
                })}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t('mobileJoin.expiredHint')}
              </Typography>
            </Stack>
          )}

          {parse.status === 'valid' && (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('mobileJoin.heading', { app: appName })}
              </Typography>

              {/*
                Host and workshop, before any of the buttons. This is the same
                information, in the same order, that the app's own confirm screen
                shows before its first network call — a lookalike domain has to
                survive a human read here too, and putting it after the primary
                action would mean nobody reads it.
              */}
              <Box sx={{ textAlign: 'center' }}>
                {parse.payload.workshopName && (
                  <Typography variant="h6" component="p" fontWeight={600}>
                    {truncate(parse.payload.workshopName, NAME_DISPLAY_MAX)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {parse.payload.apiHost}
                </Typography>
                {parse.payload.email && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {parse.payload.email}
                  </Typography>
                )}
              </Box>

              {/*
                This page hands the payload to the app over **Universal Links only**.
                There is deliberately no `os-buddy://` button here, for any kind of
                payload.

                A custom URL scheme has no owner. Any app on the device may declare
                `os-buddy`, and when more than one does, which of them iOS launches
                is undefined — Apple says so explicitly and notes that not even a
                reverse-DNS scheme prevents deliberate registration by someone else.
                Handing `invite=` over that channel means handing an
                account-creation token to whichever app answered: it could accept
                the invitation with its own password and own the new account before
                its owner ever signs in. `code=` is worse — a live session for 90
                seconds.

                An earlier cut kept the button for `pointer` payloads, on the
                grounds that a pointer carries no secret. That reasoning **fails
                open**: `kind` is decided by the *absence* of today's `invite=` and
                `code=`, while the fragment is forwarded verbatim to carry fields a
                newer staff-api might add. A future `futureCredential=` would
                therefore classify as a pointer and ride along to the unowned
                scheme, silently recreating the hole. The page cannot tell whether
                an unknown field is a secret — only the minter can — so it does not
                guess. Nothing mints pointer codes today anyway (design §9, E5), so
                the button protected nothing real.

                Universal Links have no such problem: the association is bound to a
                domain we serve, so only this app can claim `/m/join`. That is why
                the entitlement and the AASA file are a requirement, not the
                optional hardening this was first scoped as (design §5.5).

                The two channels this page *does* emit are closed by construction:
                the QR carries the fragment to our own camera, and the browser link
                carries only the explicitly extracted `inviteToken`.
              */}
              <Alert severity="info">
                {parse.payload.kind === 'invite'
                  ? t('mobileJoin.inviteAppNote', { app: appName })
                  : parse.payload.kind === 'login'
                    ? t('mobileJoin.loginAppNote', { app: appName })
                    : t('mobileJoin.pointerAppNote', { app: appName })}
              </Alert>

              {/*
                The copy-and-paste route, and the reason it is *outside* the
                desktop-only QR box below.

                A Gmail reader on a phone lands here rather than in the app, because
                Gmail rewrites hrefs to `google.com/url?q=…` and iOS matches a
                Universal Link against the initial URL of a navigation — so the app
                is never offered the link (observed on a device, 2026-07-28). For
                that reader the QR is useless (you cannot scan the screen you are
                reading) and the browser flow means retyping a password later. Copy
                → open the app → "Enter code manually" is the one route that works.
                So it must be visible at every breakpoint.

                **Invitations only.** A `code=` payload is a live session for 90
                seconds, and the whole reason `MobileJoinQr.enableCopy` defaults to
                off is that a clipboard may sync to every device on the same iCloud
                account — a trade worth making for a multi-day invitation token and
                not for a session. Rendering this unconditionally would have handed a
                login code the copy button that the portal deliberately withholds,
                which is the same policy contradicted in two places at once. A
                pointer carries no secret but has nothing to paste either, so it is
                excluded too rather than carved out.
              */}
              {parse.payload.kind === 'invite' && (
                <CopyableCode
                  value={canonicalUrl(parse.payload.fragment)}
                  copyLabel={t('mobileJoin.copyCode')}
                  copiedLabel={t('mobileJoin.codeCopied')}
                  helperText={t('mobileJoin.copyHint', { app: appName })}
                />
              )}

              {/*
                Desktop only: a QR shown on a phone cannot be scanned by the phone
                showing it. Print is off — a printed invitation QR belongs in the
                admin's dialog, where the expiry is spelled out; here it would be
                a token on paper for no one's benefit.
              */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Divider sx={{ mb: 2 }} />
                {/*
                  `email` and `expiresAt` are required props that this caller does
                  not actually use: `email` is read only by the print handler,
                  which is off here, and `expiresAt` only feeds the default
                  caption, which `caption` always replaces. Both are still passed
                  through honestly rather than faked, so nothing downstream can
                  render a value this page invented.
                */}
                <MobileJoinQr
                  mobileJoinUrl={canonicalUrl(parse.payload.fragment)}
                  email={parse.payload.email ?? ''}
                  expiresAt={parse.payload.expiresAt?.toISOString() ?? ''}
                  heading={t('mobileJoin.scanHeading')}
                  caption={
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      {parse.payload.expiresAt
                        ? t('mobileJoin.expiresNote', {
                            date: formatDate(parse.payload.expiresAt),
                          })
                        : t('mobileJoin.scanHint', { app: appName })}
                    </Typography>
                  }
                  enablePrint={false}
                />
              </Box>

              {/*
                For an invitation this is now the actionable path on a phone, not a
                footnote: if the reader is seeing this page at all, the app did not
                intercept the link, and the secret-bearing handoff above is
                deliberately not offered. So it is a real button.

                The URL is the same one the email's primary button already uses, so
                it adds no exposure that was not already there.
              */}
              {parse.payload.inviteToken && (
                <>
                  <Divider />
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t('mobileJoin.browserFallbackIntro')}
                  </Typography>
                  <Button
                    component="a"
                    href={`/accept-invitation?token=${encodeURIComponent(parse.payload.inviteToken)}`}
                    variant="contained"
                    size="large"
                    fullWidth
                  >
                    {t('mobileJoin.browserFallback')}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * The payload as the https URL a QR should carry.
 *
 * Rebuilt from `window.location` rather than reusing the address bar verbatim so
 * the encoded code is exactly `<origin>/m/join#<fragment>` — no stray query
 * string a mail gateway appended, and no dependence on the path this page
 * happened to be reached by. The app ignores this URL's own host entirely (only
 * `api=` inside the fragment is contacted), so the origin here is cosmetic; what
 * matters is that the fragment survives byte for byte.
 */
function canonicalUrl(fragment: string): string {
  return `${window.location.origin}/m/join#${fragment}`;
}
